import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { manufacturingOrderService } from "../services/manufacturingOrder.service";
import { isAdmin } from '../utils/permissions';

export const fetchManufacturingOrders = createAsyncThunk(
  "manufacturingOrder/fetchAll",
  async (_, thunkAPI) => {
    try {
      return await manufacturingOrderService.getManufacturingOrders();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch manufacturing orders");
    }
  }
);

export const fetchManufacturingOrderById = createAsyncThunk(
  "manufacturingOrder/fetchById",
  async (id, thunkAPI) => {
    try {
      return await manufacturingOrderService.getManufacturingOrderById(id);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch manufacturing order");
    }
  }
);

export const addManufacturingOrder = createAsyncThunk(
  "manufacturingOrder/add",
  async (order, thunkAPI) => {
    try {
      return await manufacturingOrderService.createManufacturingOrder(order);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to create manufacturing order");
    }
  }
);

export const editManufacturingOrder = createAsyncThunk(
  "manufacturingOrder/edit",
  async ({ id, order }, thunkAPI) => {
    try {
      return await manufacturingOrderService.updateManufacturingOrder(id, order);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update manufacturing order");
    }
  }
);

export const removeManufacturingOrder = createAsyncThunk(
  "manufacturingOrder/remove",
  async (id, thunkAPI) => {
    try {
      await manufacturingOrderService.deleteManufacturingOrder(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to delete manufacturing order");
    }
  }
);

export const cancelManufacturingOrder = createAsyncThunk(
  "manufacturingOrder/cancel",
  async (id, thunkAPI) => {
    try {
      await manufacturingOrderService.cancelManufacturingOrder(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to cancel manufacturing order");
    }
  }
);

const initialState = {
  manufacturingOrders: [],
  selectedManufacturingOrder: null,
  loading: false,
  error: null,
  pendingJobs: {},
};

const manufacturingOrderSlice = createSlice({
  name: "manufacturingOrder",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedManufacturingOrder: (state) => {
      state.selectedManufacturingOrder = null;
    },


    manufacturingOrderUpdateSuccess: (state, action) => {
      const { jobId, result } = action.payload;

      if (jobId) {
        delete state.pendingJobs[jobId];
      }

      if (result) {
        state.manufacturingOrders = state.manufacturingOrders.map((order) =>
          order.manufacturingOrderId === result.manufacturingOrderId
            ? result
            : order
        );

        if (
          state.selectedManufacturingOrder &&
          state.selectedManufacturingOrder.manufacturingOrderId === result.manufacturingOrderId
        ) {
          state.selectedManufacturingOrder = result;
        }
      }
    },

    manufacturingOrderUpdateFailed: (state, action) => {
      const { jobId, error } = action.payload;

      if (jobId) {
        delete state.pendingJobs[jobId];
      }

      state.error = error;
    },
  },

    extraReducers: (builder) => {
      builder
        // Fetch all manufacturing orders
        .addCase(fetchManufacturingOrders.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchManufacturingOrders.fulfilled, (state, action) => {
          state.loading = false;
          state.manufacturingOrders = Array.isArray(action.payload)
            ? action.payload
            : action.payload?.items || [];
        })
        .addCase(fetchManufacturingOrders.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        // Fetch manufacturing order by ID
        .addCase(fetchManufacturingOrderById.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchManufacturingOrderById.fulfilled, (state, action) => {
          state.loading = false;
          state.selectedManufacturingOrder = action.payload;
        })
        .addCase(fetchManufacturingOrderById.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        // Add manufacturing order
        .addCase(addManufacturingOrder.pending, (state) => {
          state.loading = true;
        })
        .addCase(addManufacturingOrder.fulfilled, (state, action) => {
          state.loading = false;
          state.manufacturingOrders.push(action.payload);
        })
        .addCase(addManufacturingOrder.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
     
        .addCase(editManufacturingOrder.pending, (state) => {
          state.loading = true;
        })
        .addCase(editManufacturingOrder.fulfilled, (state, action) => {
          state.loading = false;

          // On track le job en attente, la mise à jour réelle viendra via SignalR
          const { jobId, orderId } = action.payload;
          if (jobId) {
            state.pendingJobs[jobId] = {
              orderId,
              startedAt: Date.now(),
            };
          }
        })
        .addCase(editManufacturingOrder.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        // Remove manufacturing order
        .addCase(removeManufacturingOrder.pending, (state) => {
          state.loading = true;
        })
        .addCase(removeManufacturingOrder.fulfilled, (state, action) => {
          state.loading = false;
          state.manufacturingOrders = state.manufacturingOrders.filter(
            (order) => order.manufacturingOrderId !== action.payload
          );
        })
        .addCase(removeManufacturingOrder.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        // Cancel manufacturing order
        .addCase(cancelManufacturingOrder.pending, (state) => {
          state.loading = true;
        })
        .addCase(cancelManufacturingOrder.fulfilled, (state, action) => {
          state.loading = false;
          state.manufacturingOrders = state.manufacturingOrders.filter(
            (order) => order.manufacturingOrderId !== action.payload
          );
        })
        .addCase(cancelManufacturingOrder.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        });
      }
  });

export const {
  clearError,
  clearSelectedManufacturingOrder,
  manufacturingOrderUpdateSuccess,
  manufacturingOrderUpdateFailed,
} = manufacturingOrderSlice.actions;

// Selectors
/**
 * Selector: Get manufacturing orders accessible to the user
 * Filters orders where user has access to the shop OR lab
 */
export const selectAccessibleManufacturingOrders = createSelector(
  [(state) => state.manufacturingOrder.manufacturingOrders, (state) => state.auth.user],
  (allOrders, currentUser) => {
    if (!currentUser) return [];

    // ADMIN can see all manufacturing orders
    if (isAdmin(currentUser)) {
      return allOrders;
    }

    // Non-admin users only see orders from their assigned shops/labs
    // shopIds includes both shops and labs
    return allOrders.filter(order =>
      currentUser.shopIds && (
        currentUser.shopIds.includes(order.shopId) ||
        currentUser.shopIds.includes(order.labId)
      )
    );
  }
);

export const selectIsJobPending = (jobId) => (state) =>
  Boolean(state.manufacturingOrder.pendingJobs[jobId]);

export const selectPendingJobsCount = (state) =>
  Object.keys(state.manufacturingOrder.pendingJobs).length;

export const selectPendingManufacturingOrderIds = createSelector(
  [(state) => state.manufacturingOrder.pendingJobs],
  (pendingJobs) => {
    return [
      ...new Set(
        Object.values(pendingJobs)
          .map((job) => job?.orderId)
          .filter(Boolean)
      ),
    ];
  }
);

export const selectPendingManufacturingOrdersMap = createSelector(
  [(state) => state.manufacturingOrder.pendingJobs],
  (pendingJobs) => {
    return Object.values(pendingJobs).reduce((acc, job) => {
      if (job?.orderId) {
        acc[job.orderId] = true;
      }
      return acc;
    }, {});
  }
);

export const selectIsManufacturingOrderProcessing = (orderId) =>
  createSelector(
    [(state) => state.manufacturingOrder.pendingJobs],
    (pendingJobs) =>
      Object.values(pendingJobs).some((job) => job?.orderId === orderId)
  );

export default manufacturingOrderSlice.reducer;
